const getTop = (element: Element): number => element.getBoundingClientRect().top;

/**
 * Resolves once the element's vertical position has been stable for `stableDuration` ms,
 * or after `maxWait` ms regardless — whichever comes first.
 *
 * Useful before scrolling to an element that may still be animating or reflow-ing.
 *
 * @param element - The element to observe.
 * @param stableDuration - How long (ms) the position must remain unchanged. Default: `100`.
 * @param maxWait - Hard timeout (ms) after which the promise resolves unconditionally. Default: `500`.
 */
export const waitForStableTop = (element: Element, stableDuration = 100, maxWait = 500): Promise<void> =>
	new Promise((resolve) => {
		const startTime = Date.now();
		let lastTop = getTop(element);
		let stableSince = startTime;

		const poll = (): void => {
			const now = Date.now();
			const top = getTop(element);

			if (top !== lastTop) {
				lastTop = top;
				stableSince = now;
			}

			const isStable = now - stableSince >= stableDuration;
			const isTimedOut = now - startTime >= maxWait;

			if (isStable || isTimedOut) {
				resolve();
				return;
			}

			requestAnimationFrame(poll);
		};

		poll();
	});

/**
 * Scrolls the given element into view, waiting for fonts and layout stability first.
 *
 * @param element - Element, element id string, or `null` (no-op).
 */
export const scrollIntoView = async (element: string | Element | null): Promise<void> => {
	if (!element) return;

	// Resolve a string id to an actual element; bail if not found
	if (typeof element === "string") {
		element = document.getElementById(element);
		if (!element) return;
	}

	try {
		// Wait for custom fonts so the rendered layout is final before measuring positions
		if (document.fonts) {
			await document.fonts.ready.catch(() => undefined);
		}

		// Wait until the element's top position has settled (e.g. after animations or reflows)
		await waitForStableTop(element);
	} catch {
		// Don't throw for legacy browsers;
	}

	try {
		element.scrollIntoView({
			block: "start",
			behavior: "smooth",
		});
	} catch {
		// No options alternative for legacy browsers
		element.scrollIntoView();
	}
};

/**
 * Schedules `scrollIntoView` after a `delay` and returns a cleanup function that cancels it.
 * Intended for use in React `useEffect` callbacks.
 *
 * @param element - Element, element id string, or `null`.
 * @param delay - Delay in ms before scrolling. Default: `250`.
 * @returns Cleanup function that cancels the pending scroll.
 */
export const scrollIntoViewEffect = (element: string | Element | null, delay = 250) => {
	const _timeout = setTimeout(() => {
		void scrollIntoView(element);
	}, delay);
	return () => clearTimeout(_timeout);
};
